import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './sidebar';
import Topbar from './topbar';
import ActionBar from './ActionBar';
import { useToast } from '../Submissions/Toast';
import { fetchPackages } from '../../api/packagesApi';
import '../styles/layout/main.css';

// ── خريطة المسارات إلى العناوين ─ـ
const pathToTitleMap = {
  '/': 'dashboard',
  '/Speakersummary': 'speaker_profile',
  '/speakermanagement': 'speaker_management',
  '/purchases': 'active_purchases',
  '/author-services': 'author_services',
  '/packages': 'select_package',
  '/submissions': 'manage_submissions',
  '/letters': 'letters_certificates',
  '/invoices': 'invoices_payments',
  '/network': 'my_network',
  '/profile': 'my_profile',
  '/support': 'support',
  '/ResetPassword': 'reset_password',
  '/Publications': 'publications',
  '/DeleteAccount': 'delete_account',
  '/notifications': 'notifications',
  '/social-wall': 'social_wall',
};

const Layout = ({ children, user }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation('common');
  const { addToast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Group Ticket State
  const [isGroupTicketOpen, setIsGroupTicketOpen] = useState(false);
  const [totalPeople, setTotalPeople] = useState('5');
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [conferencesList, setConferencesList] = useState([]);
  const [isSubmittingGroupTicket, setIsSubmittingGroupTicket] = useState(false);
  const [groupTicketError, setGroupTicketError] = useState(null);

  useEffect(() => {
    fetchPackages().then(res => {
      const uniqueConfs = [];
      const seenConfs = new Set();
      (res.packagesList || []).forEach(pkg => {
        const confId = String(pkg.chair_id || pkg.event_id || pkg.eventId || pkg.chairId || '');
        if (!confId) return;
        if (!seenConfs.has(confId)) {
          seenConfs.add(confId);
          uniqueConfs.push({
            id: confId,
            name: pkg.name || pkg.title || 'Conference',
            acronym: pkg.acronym || 'CONF'
          });
        }
      });
      setConferencesList(uniqueConfs);
    }).catch(() => { });
  }, []);

  // Parse Profile Details safely
  const getProfileData = () => {
    try {
      const storedProfile = localStorage.getItem("user_profile");
      const storedDetails = localStorage.getItem("user_details");

      const profile = storedProfile ? JSON.parse(storedProfile) : {};
      const details = storedDetails ? JSON.parse(storedDetails) : {};

      const name = profile?.name || user?.name || '';
      const lname = profile?.lname || user?.lname || '';
      const email = profile?.email || user?.email || '';
      const phone = profile?.phone || details?.phone || profile?.usr_det?.contact || details?.contact || profile?.usr_det?.phone || '';
      const phoneCode = profile?.phoneCode || details?.phoneCode || '';

      return {
        fullName: [name, lname].filter(Boolean).join(' ') || 'User',
        email,
        phone: [phoneCode, phone].filter(Boolean).join(' ') || 'N/A'
      };
    } catch (e) {
      return {
        fullName: user?.name || 'User',
        email: user?.email || '',
        phone: 'N/A'
      };
    }
  };

  const profileData = getProfileData();
  const isRTL = i18n.language === 'ar';

  // ── تهيئة العنوان من المسار الحالي (يعمل عند الرفرش) ─ـ
  const [currentPageTitle, setCurrentPageTitle] = useState(() => {
    return pathToTitleMap[location.pathname] || 'dashboard';
  });

  // ─ـ الحصول على الصفحة النشطة من المسار ─ـ
  const getCurrentPage = () => {
    return pathToTitleMap[location.pathname] || 'dashboard';
  };

  const openSidebar = () => setSidebarOpen(true);
  const closeSidebar = () => setSidebarOpen(false);

  // ─ـ تحديث العنوان عند تغيير المسار (بما في ذلك الرفرش) ─ـ
  const handlePageChange = useCallback((newTitle) => {
    setCurrentPageTitle(newTitle);
  }, []);

  // ─ـ مزامنة العنوان مع المسار عند الرفرش أو التنقل المباشر ─ـ
  useEffect(() => {
    const mappedTitle = pathToTitleMap[location.pathname];
    if (mappedTitle) {
      setCurrentPageTitle(mappedTitle);
    }
  }, [location.pathname]);

  // Global event listener to open Group Ticket Modal from children pages
  useEffect(() => {
    const handleOpen = () => setIsGroupTicketOpen(true);
    window.addEventListener('open-group-ticket', handleOpen);
    return () => window.removeEventListener('open-group-ticket', handleOpen);
  }, []);

  const handleGroupTicketSubmit = async (e) => {
    e.preventDefault();
    if (!totalPeople || parseInt(totalPeople, 10) < 5) {
      setGroupTicketError('Minimum quantity for group tickets is 5');
      return;
    }

    setIsSubmittingGroupTicket(true);
    setGroupTicketError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('https://emiratesscholar.com/system/api/send-group-ticket-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          conference_id: selectedConferenceId,
          total_people: parseInt(totalPeople, 10),
          name: profileData.fullName,
          email: profileData.email,
          contact: profileData.phone
        })
      });

      if (response.ok) {
        addToast(t('request_sent_success', 'Your group ticket request has been sent successfully!'), 'success');
        setIsGroupTicketOpen(false);
        setTotalPeople('5');
        setSelectedConferenceId('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setGroupTicketError(errorData?.message || t('error_try_again', 'An error occurred. Please try again.'));
      }
    } catch (err) {
      console.error('Error sending group ticket request:', err);
      setGroupTicketError(t('error_try_again', 'An error occurred. Please try again.'));
    } finally {
      setIsSubmittingGroupTicket(false);
    }
  };

  return (
    <div className="esc-shell">
      {/* Sidebar - Fixed Left */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        user={user}
        currentPage={getCurrentPage()}
        onPageChange={handlePageChange}
        onGroupTicketOpen={() => setIsGroupTicketOpen(true)}
      />

      {/* Main Content Area */}
      <div className="esc-main">
        {/* Topbar - Sticky Top with Language/Logout */}
        <Topbar
          pageTitle={currentPageTitle}
          onMenuClick={openSidebar}
          onPageChange={handlePageChange}
          user={user}
        />

        {/* ActionBar - Scrolls with page */}
        <ActionBar />

        {/* Page Content */}
        <div className="esc-content">
          {children}
        </div>
      </div>

      {/* Group Ticket Popup Modal */}
      {isGroupTicketOpen && (
        <div
          onClick={() => setIsGroupTicketOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            backdropFilter: 'blur(3px)',
            boxSizing: 'border-box'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '460px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              boxSizing: 'border-box'
            }}
          >
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #185FA5, #0C447C)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="material-icons">confirmation_number</span>
                <span style={{ fontWeight: '700', fontSize: '16px' }}>{t('request_group_ticket', 'Request Group Ticket')}</span>
              </div>
              <button
                onClick={() => setIsGroupTicketOpen(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleGroupTicketSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box', textAlign: isRTL ? 'right' : 'left' }}>
              {groupTicketError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px', color: '#dc2626', fontSize: '13px' }}>
                  {groupTicketError}
                </div>
              )}

              {/* Name (Read-Only) */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>{t('name', 'Name')}</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  disabled
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#f8fafc', color: '#64748b', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Email (Read-Only) */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>{t('email', 'Email')}</label>
                <input
                  type="text"
                  value={profileData.email}
                  disabled
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#f8fafc', color: '#64748b', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Contact Phone (Read-Only) */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>{t('contact', 'Contact Phone')}</label>
                <input
                  type="text"
                  value={profileData.phone}
                  disabled
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#f8fafc', color: '#64748b', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Select Conference */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  {t('conference', 'Conference')} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={selectedConferenceId}
                  onChange={(e) => setSelectedConferenceId(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                >
                  <option value="">{t('choose_conference', 'Select a Conference')}</option>
                  {conferencesList.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.acronym ? `(${c.acronym})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Total People / Tickets (Input - min 5) */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  {t('num_people', 'Number of Tickets (Min. 5)')} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  min="5"
                  required
                  value={totalPeople}
                  onChange={(e) => setTotalPeople(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Footer Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px', justifyContent: isRTL ? 'flex-start' : 'flex-end', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  type="button"
                  onClick={() => setIsGroupTicketOpen(false)}
                  style={{ padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: '8px', color: '#475569', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                >
                  {t('close', 'Close')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingGroupTicket}
                  style={{
                    padding: '10px 24px',
                    background: 'linear-gradient(135deg, #185FA5, #0C447C)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: isSubmittingGroupTicket ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmittingGroupTicket ? (
                    <><div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'layout-spin 0.6s linear infinite' }} /> Sending...</>
                  ) : t('submit_request', 'Submit Request')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`
        @keyframes layout-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default Layout;